import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
  video,
} from '../helpers';

const posPerm = permissionTable([
  [
    'manage_pos_restaurant',
    'Required for restaurant/POS floor ops, menu, orders, tables, rooms, and payments',
  ],
]);

export const posGuides = [
  buildGuide({
    id: 'pos-overview',
    title: 'Restaurant & POS Overview',
    moduleId: 'pos',
    summary:
      'Run room service and table service: orders, KOT, billing, menu, and QR ordering.',
    keywords: ['pos', 'restaurant', 'kot', 'food', 'room service'],
    pathHint: 'Hotel → Restaurant / POS',
    related: [
      'restaurant-ops',
      'pos-menu',
      'pos-orders',
      'pos-qr-ordering',
      'check-out',
    ],
    videos: [
      video(
        'NGoVXofvZQc',
        'Restaurant POS software demo',
        'Complete POS walkthrough covering billing, orders, and sales analytics.'
      ),
    ],
    overview: [
      ...paragraphs(
        'Roomnexa POS covers restaurant floor operations and admin setup. Guests order to rooms or tables; kitchen receives KOT-style order flow; cashiers settle bills; menus and QR sessions are configured in POS admin.',
        'Front office and F&B must work together: room-service and room-charge bills attach to in-house stays and must be settled before or at Check-out. The classic failure is checking a guest out while breakfast is still open on the POS.',
        'Example service path: In-house guest in 204 calls for dinner → Restaurant ops selects room 204 → add items → send KOT to kitchen → deliver → charge to room or collect cash → order shows paid on POS Orders. At checkout next morning, FO sees a clear folio.',
        'Admin setup order matters: build Menu (categories → items → variations), enable POS Rooms for room service, create Tables for dine-in, train cashiers on the order drawer, then optionally launch QR Open Ordering.',
        'POS Dashboard and restaurant reports are for F&B performance — they do not replace room revenue dashboards. Use them with Cash Counter reconciliation at shift end.'
      ),
      table(
        ['Area', 'Use it for', 'Typical user'],
        [
          [
            'Restaurant ops',
            'Live floor: rooms/tables, order drawer, timers, merge, print/KOT',
            'Cashiers / captains',
          ],
          ['POS Dashboard', 'Sales analytics', 'F&B manager'],
          [
            'Orders',
            'Order list, payments, e-bill, audit',
            'Cashier / supervisor',
          ],
          [
            'Rooms / Tables',
            'Enable room service rooms; manage tables + QR',
            'F&B admin',
          ],
          ['Menu', 'Categories, items, variations', 'F&B admin'],
          [
            'QR Open Ordering',
            'Guest self-order via public QR links',
            'Guests + monitoring staff',
          ],
        ]
      ),
    ],
    beforeYouBegin: [
      posPerm,
      bullets([
        'Menu items created and active',
        'Tables created; rooms enabled for room service',
        'Printers / KOT process agreed with kitchen',
        'Know which tenders you accept (cash, UPI, card, room charge)',
        'FO informed how room charges appear at checkout',
      ]),
      callout(
        'important',
        'Module visibility',
        'If Restaurant/POS is missing, check hotel package and manage_pos_restaurant permission — it is not a Booking menu item.'
      ),
    ],
    steps: [
      heading('Launch checklist'),
      steps([
        {
          text: 'Configure Menu: categories first, then items with prices, then variations (Half/Full, etc.).',
        },
        {
          text: 'Enable Rooms for room service under POS → Room; create Tables under POS → Table.',
        },
        {
          text: 'Agree kitchen process: who prints/sends KOT, how mods are called back, when to mark served.',
        },
        {
          text: 'Train cashiers on Restaurant ops order drawer: add items → KOT → pay → e-bill.',
        },
        {
          text: 'Run a test order on a table and a test room-service order to an in-house room.',
        },
        {
          text: 'Optional: activate QR sessions for tables and test scan → menu → checkout before printing tent cards.',
        },
        {
          text: 'At shift end, zero unpaid orders on POS → Orders and reconcile with Cash Counter.',
        },
        {
          text: 'Before FO night close, clear open room POS bills so checkouts tomorrow are clean.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Restaurant landing / floor board',
          'POS admin tabs (Dashboard, Orders, Menu, Room, Table)',
          'Order drawer and payment drawer',
          'Public QR menu pages (guest-facing)',
        ],
        'Main POS surfaces'
      ),
      fieldTable([
        ['Restaurant ops', '—', 'Live taking of orders / KOT / settle'],
        ['Menu', 'Setup', 'What can be sold'],
        ['Orders', '—', 'Search, pay, e-bill, audit'],
        ['Rooms / Tables', 'Setup', 'Where orders can be placed'],
        ['POS Dashboard', '—', 'F&B KPIs'],
      ]),
    ],
    workflow: [
      workflow('Dine-in order', [
        'Open table',
        'Add items',
        'Send KOT',
        'Serve',
        'Settle bill',
        'Print / e-bill',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Orders post sales into restaurant payments. Room-charge orders may attach to in-house bookings and must be settled before or at checkout so folios match reality.',
        'KOT is the kitchen signal — sending items twice without intent creates double preparation. Merging tables combines balances and is hard to untangle during peak.',
        'Menu active flags control what appears on new orders and QR menus; historical orders keep the lines that were sold.',
        'Refunds and voids need supervisor control — they reverse revenue and appear in order audit and restaurant reports.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'FO coordination',
        'Before checkout, cashiers should clear open room POS bills. Make it part of morning departure prep.'
      ),
      callout(
        'tip',
        'Test before QR print',
        'Never laminate QR tent cards until a real scan opens the correct table menu with live items.'
      ),
      callout(
        'note',
        'Kitchen display',
        'Roomnexa uses order/KOT workflows in restaurant ops; use your printer/KDS process alongside the order list.'
      ),
    ],
    bestPractices: [
      bullets([
        'Keep menu prices and tax policy consistent with Finance Setup.',
        'Void/comp only with supervisor role.',
        'Call back orders to guests before sending KOT.',
        'Reconcile POS payments with Cash Counter daily.',
        'Disable OOO rooms in POS Rooms so charges cannot hit dead rooms.',
        'Brief night FO on any unpaid room-service tabs.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Taking cash offline and forgetting to settle in POS.',
        'Merging wrong tables.',
        'Checking out guests with open POS bills.',
        'Sending KOT twice for the same items.',
        'Charging the wrong room for room service.',
        'Printing QR codes before the menu is ready.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Module missing',
            'Package or permission',
            'Enable package + manage_pos_restaurant',
          ],
          [
            'No items on POS',
            'Menu empty or inactive',
            'Add/activate Menu items first',
          ],
          [
            'Room not listed for service',
            'Not enabled in POS Rooms',
            'Enable under POS → Room',
          ],
          [
            'Checkout blocked by F&B',
            'Open unpaid room order',
            'Settle on POS Orders / restaurant ops first',
          ],
          [
            'Kitchen did not get order',
            'KOT not sent / printer',
            'Resend/print KOT; verify printer process',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is kitchen display included?',
          a: 'Roomnexa uses order/KOT workflows in restaurant ops; use your printer/KDS process alongside the order list.',
        },
        {
          q: 'Is QR ordering the same as NexaStay?',
          a: 'No. NexaStay is the hotel booking engine. QR ordering is restaurant POS self-order.',
        },
        {
          q: 'Can POS charge to a reserved (not checked-in) room?',
          a: 'Room service typically targets in-house rooms — follow what Restaurant ops allows and your SOP.',
        },
        {
          q: 'Where do managers see sales?',
          a: 'POS Dashboard for trends; Reports → Restaurant for accountant-ready detail.',
        },
        {
          q: 'Who should own voids?',
          a: 'Supervisors with clear audit. Cashiers should not unlimited-comp.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'restaurant-ops',
    title: 'Restaurant Floor Operations',
    moduleId: 'pos',
    summary:
      'Take orders on rooms and tables, manage KOT timing, merge bills, and print.',
    keywords: [
      'restaurant',
      'floor',
      'order drawer',
      'merge bill',
      'kot',
    ],
    pathHint: 'Restaurant → Floor ops',
    related: ['pos-orders', 'pos-tables', 'pos-rooms', 'pos-payments'],
    overview: [
      ...paragraphs(
        'Restaurant landing is the live operations screen for F&B staff: select a room or table, open the order drawer, send items to kitchen (KOT), track timers, merge bills when guests combine, and print or settle.',
        'This is the screen you use during service — not Menu setup. If items are missing here, fix Menu active flags first, then refresh the floor screen.',
        'Example dine-in: Guests sit at T5 → open table T5 → add 2× Masala Dosa + Filter Coffee → send KOT → kitchen cooks → mark served → present bill → capture UPI in payment drawer → e-bill optional.',
        'Example room service: In-house 118 requests club sandwich → select room 118 → add item with notes (“no mayo”) → KOT → deliver → charge to room or collect → FO will see it at checkout if room-charged.',
        'Merge bill is powerful and dangerous: confirm both parties agree before combining. Wrong merges during peak are hard to untangle and create payment disputes.'
      ),
    ],
    beforeYouBegin: [
      posPerm,
      bullets([
        'Menu active; tables/rooms enabled',
        'Know table map / room numbers matching physical stickers',
        'Kitchen ready to receive KOT/print',
        'For room service, guest should be in-house per SOP',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open Restaurant operations (floor landing).' },
        {
          text: 'Select a table or in-house room from the board.',
        },
        {
          text: 'Open the order drawer and add menu items (and variations). Add clear kitchen notes for allergies/mods.',
        },
        {
          text: 'Read the order back to the guest, then send/print KOT for the kitchen.',
        },
        {
          text: 'Add more courses as needed; use timers to track preparation; mark served when delivered.',
        },
        {
          text: 'If guests combine tables, use merge only after both parties agree — verify items and balances before merging.',
        },
        {
          text: 'When dining ends, open payment: choose tender (cash/card/UPI/room charge), capture amount, confirm.',
        },
        {
          text: 'Print bill or send e-bill as required; confirm the table/room session is closed/paid.',
        },
        {
          text: 'If something fails, open the order from POS → Orders for payment retry, audit, or supervisor help.',
        },
      ]),
      callout(
        'warning',
        'Merge bill',
        'Confirm both parties agree before merging. Merges are hard to untangle during peak.'
      ),
    ],
    screens: [
      bullets(
        [
          'Rooms / tables board',
          'Order drawer',
          'Item search / categories',
          'KOT / print actions',
          'Timers / served markers',
          'Merge controls',
          'Payment entry',
        ],
        'Floor screen areas'
      ),
      fieldTable([
        ['Table / room selector', 'Yes', 'Where the order belongs'],
        ['Menu items / variations', 'Yes', 'What is sold on the ticket'],
        ['Kitchen notes', 'Recommended', 'Mods, allergies, timing'],
        ['KOT / print', 'Yes to cook', 'Signals kitchen'],
        ['Merge', 'Optional', 'Combines bills carefully'],
        ['Payment', 'To close', 'Settles the order'],
      ]),
    ],
    workflow: [
      workflow('Room service', [
        'In-house request',
        'Select room',
        'Add items',
        'Send KOT',
        'Deliver',
        'Charge room or collect',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Open orders occupy the table/room session until paid or cancelled per rules. Leaving tables open blocks the next seating.',
        'KOT communicates preparation intent. Duplicate KOTs without removing lines cause double cook — coordinate with kitchen if you must reprint.',
        'Room charge posts restaurant spend toward the in-house booking folio path your property uses; cashiers and FO must both understand it.',
        'Merge combines balances carefully — reverse only with authorisation. Prefer correct table selection over merge-as-fix.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Call back the order',
        'Call back items and mods before KOT. It is faster than voiding wrong kitchen tickets.'
      ),
      callout(
        'tip',
        'Wrong room risk',
        'For room service, confirm the room number with the guest twice — charging 204 instead of 205 is a checkout nightmare.'
      ),
      callout(
        'note',
        'Timers',
        'Mark served honestly so delay reports and captain follow-ups stay meaningful.'
      ),
    ],
    bestPractices: [
      bullets([
        'Call back the order to the guest.',
        'Mark served to keep timers honest.',
        'Keep one captain owning merges and comps.',
        'Never leave cash in the drawer without capturing payment in POS.',
        'For large tables, confirm headcount and course timing before firing everything.',
        'Close paid tables promptly so the board shows true availability.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Sending KOT twice for the same items.',
        'Charging the wrong room.',
        'Merging without guest consent.',
        'Taking payment offline only.',
        'Starting a second order on a table that already has an open ticket.',
        'Forgetting kitchen notes for allergies.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Table locked',
            'Existing open order',
            'Finish/settle existing order before new seating',
          ],
          [
            'Items missing',
            'Menu inactive / wrong category',
            'Check Menu active flags; refresh floor screen',
          ],
          [
            'Cannot charge room',
            'Room not enabled or not in-house',
            'Enable POS Rooms; confirm check-in; else collect tender',
          ],
          [
            'Kitchen missing ticket',
            'KOT not sent / print fail',
            'Resend/print; verify printer; read order aloud to kitchen',
          ],
          [
            'Merge went wrong',
            'Wrong tables selected',
            'Stop; supervisor + order audit; avoid further merges',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I split bills?',
          a: 'Use payment flows to collect partial tenders where supported; confirm with your property SOP for split cheques.',
        },
        {
          q: 'Where do I find an old order?',
          a: 'POS → Orders with date/table/room filters.',
        },
        {
          q: 'What is KOT?',
          a: 'Kitchen Order Ticket — the signal/print that tells kitchen what to prepare from the POS order.',
        },
        {
          q: 'Can I take orders without printing?',
          a: 'Only if kitchen monitors the order list/KDS reliably. Most hotels still print or explicitly send KOT.',
        },
        {
          q: 'Room service when restaurant is closed?',
          a: 'Follow property hours. If you still take an order, ensure kitchen staffing exists — POS will not cook for you.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pos-menu',
    title: 'Menu Management',
    moduleId: 'pos',
    summary:
      'Create categories, items, and variations that appear on POS and QR menus.',
    keywords: [
      'menu',
      'categories',
      'items',
      'variations',
      'modifiers',
    ],
    pathHint: 'POS → Menu',
    related: ['pos-overview', 'pos-qr-ordering', 'restaurant-ops'],
    overview: [
      ...paragraphs(
        'Menu configuration is the product catalogue for restaurant selling. Categories organise items on the cashier screen and QR menu; items carry names and prices; variations cover sizes or common options (Half/Full, spice level).',
        'Build structure before service: Categories → Items → Variations. Cashiers cannot sell what is inactive or never created — “item missing on POS” is almost always a menu setup issue.',
        'Example: Create category “South Indian,” add item “Masala Dosa” with price, add variations “Plain / Cheese” if priced differently, keep item Active. Deactivate “Pumpkin Soup” in summer instead of deleting history.',
        'QR Open Ordering reads the same active catalogue. If QR menu looks empty, fix Menu before reprinting QR codes.',
        'Keep naming dual-friendly: guest-clear on QR, kitchen-familiar for KOT. Duplicate spellings (“Coke” vs “Coca Cola”) break reporting and confuse cashiers.'
      ),
    ],
    beforeYouBegin: [
      posPerm,
      bullets([
        'Finalise prices with F&B and finance (tax inclusive policy clear)',
        'List categories that match how captains think (Starters, Mains, Beverages…)',
        'Know which items need variations vs simple single price',
        'Plan seasonal items as deactivate/reactivate, not delete',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open POS → Menu.' },
        {
          text: 'Create categories (Starters, Mains, Beverages, Room Service Specials…).',
        },
        {
          text: 'Add items with name, price, and category.',
        },
        {
          text: 'Add variations where needed (Half/Full, spicy levels, size).',
        },
        {
          text: 'Activate items you want sellable; deactivate seasonal items instead of deleting history.',
        },
        {
          text: 'Brief cashiers on new names and 86’d (unavailable) items for the shift.',
        },
        {
          text: 'Verify items appear on Restaurant ops and, if used, on QR menu after refresh.',
        },
        {
          text: 'Align tax treatment with Finance Setup so invoices and reports stay consistent.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Category', 'Yes', 'Grouping on POS and QR'],
        ['Item name', 'Yes', 'Guest-facing / cashier name'],
        ['Price', 'Yes', 'Base sell price'],
        ['Variations', 'No', 'Option prices/names'],
        ['Active', 'Yes', 'Visibility on new orders'],
      ]),
    ],
    workflow: [
      workflow('Menu update', [
        'Plan change',
        'Edit item / price',
        'Activate',
        'Brief cashiers',
        'Verify on floor & QR',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Inactive items stop appearing on new orders and QR menus but historical orders keep past lines for audit and reports.',
        'Price changes affect new sales; already sent KOTs/orders keep their captured lines — communicate mid-service changes carefully.',
        'Variations are the practical way to handle common modifiers without an endless free-text chaos; keep the list short for cashier speed.',
        'Finance Setup tax configuration should match how you quote menu prices to avoid checkout confusion on e-bills.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Naming',
        'Use kitchen-familiar short names plus clear guest names. Avoid cryptic codes on QR menus.'
      ),
      callout(
        'tip',
        '86 board',
        'When an item runs out, deactivate or brief cashiers immediately so KOT does not fire impossible tickets.'
      ),
      callout(
        'note',
        'Photos',
        'Photo menus for QR branding are separate from item price setup — still keep names aligned.'
      ),
    ],
    bestPractices: [
      bullets([
        'Review taxes with Finance Setup.',
        'Photo menus separately if using QR branding.',
        'Deactivate instead of delete for seasonal items.',
        'Avoid duplicate items with slightly different spellings.',
        'Test one order after every major price change.',
        'Keep variation lists short — slow cashiers lose tables.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Duplicate items with slightly different spellings.',
        'Leaving items inactive and blaming “POS bug.”',
        'Changing price mid-rush without telling cashiers.',
        'Putting every modifier as a separate item instead of variations.',
        'Deleting items that exist on open orders.',
        'QR still showing old items because sessions were not retested.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Item not on POS',
            'Inactive or wrong category',
            'Active + correct category; refresh floor screen',
          ],
          [
            'QR menu empty',
            'No active items / session issue',
            'Activate items; retest QR session',
          ],
          [
            'Wrong price charged',
            'Old variation / stale screen',
            'Verify menu price; refresh; void with supervisor if needed',
          ],
          [
            'Cannot add category',
            'Permission',
            'Confirm manage_pos_restaurant',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Are modifiers supported?',
          a: 'Use variations/options on items for common modifiers; keep the list practical for cashiers. Free-text kitchen notes handle one-off requests.',
        },
        {
          q: 'Does Menu change affect past orders?',
          a: 'Historical order lines remain as sold. New orders use current menu.',
        },
        {
          q: 'Can I have different menus for room service?',
          a: 'Organise with categories/items your property treats as room-service; follow what your deployment shows — do not invent separate engines.',
        },
        {
          q: 'Who approves price changes?',
          a: 'F&B manager with finance alignment — not ad-hoc cashier edits during service.',
        },
        {
          q: 'Why keep inactive items?',
          a: 'Reporting and reorder speed for seasonal return; deletion loses clarity in history.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pos-orders',
    title: 'POS Orders',
    moduleId: 'pos',
    summary:
      'Find restaurant orders, review payments, e-bills, drafts, and audit history.',
    keywords: [
      'pos orders',
      'order list',
      'e-bill',
      'restaurant orders',
    ],
    pathHint: 'POS → Orders',
    related: ['pos-payments', 'restaurant-ops', 'reports-restaurant'],
    overview: [
      ...paragraphs(
        'Orders tab is the back-office list of restaurant tickets for search, settlement follow-up, e-bill/draft invoice actions, and audit. Use it when the floor is busy or when FO asks “is Room 204’s dinner paid?”',
        'Filter by date, status, room/table, or order id. Unpaid filters are your end-of-day lifesaver — never close Cash Counter with mystery open tickets.',
        'Example checkout coordination: FO is checking out 204. You filter today’s orders for room 204, find an unpaid breakfast, capture payment or confirm room charge, send e-bill if needed, then tell FO the folio is clear.',
        'E-bill is the guest-facing settlement copy; draft invoice tools support printable/fiscal needs depending on setup. Order audit helps investigate voids, refunds, and disputes.',
        'Order payment states (paid/failed/refunded) drive restaurant revenue reporting — fixing status correctly matters more than “making the table free” verbally.'
      ),
    ],
    beforeYouBegin: [
      posPerm,
      bullets([
        'Know the date range and room/table to search',
        'Supervisor available for refunds/voids',
        'Understand e-bill vs draft invoice for your property',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open POS → Orders.' },
        {
          text: 'Filter by date, status, room/table, or order id.',
        },
        {
          text: 'Open an order to review lines and payment state.',
        },
        {
          text: 'Capture payment if unpaid (see Restaurant Payments).',
        },
        {
          text: 'Send e-bill or use draft invoice tools as required for the guest.',
        },
        {
          text: 'For disputes, open audit and involve a supervisor before refunding.',
        },
        {
          text: 'At end of day, list unpaid orders and settle or document exceptions.',
        },
        {
          text: 'Reconcile paid totals with Cash Counter / payments reports.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Orders table',
          'Filters',
          'Payment drawer',
          'E-bill / draft invoice actions',
          'Audit',
        ],
        'Orders screen areas'
      ),
      fieldTable([
        ['Date / status filters', '—', 'Find the ticket quickly'],
        ['Room / table / order id', '—', 'Operational lookup keys'],
        ['Line items', '—', 'What was ordered'],
        ['Payment state', '—', 'Paid / unpaid / failed / refunded'],
        ['E-bill / draft invoice', 'Policy', 'Guest documents'],
        ['Audit', 'When investigating', 'Who changed what'],
      ]),
    ],
    workflow: [
      workflow('End-of-day F&B', [
        'List today’s orders',
        'Find unpaid',
        'Settle',
        'Send e-bills if needed',
        'Reconcile with cash counter',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Order payment states drive restaurant revenue reporting and what FO can trust on the guest folio for room charges.',
        'Failed payments need retry or alternate tender — do not mark tables free without a successful capture when policy requires payment.',
        'Refunds reverse revenue and must stay audited. Casual “cash back” without POS refund breaks Cash Counter.',
        'Draft invoice vs e-bill serves different document needs; use the one your finance setup expects for GST/guest copies.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Audit',
        'Use order audit when investigating voids or disputes — do not argue from memory.'
      ),
      callout(
        'tip',
        'FO hotline',
        'When FO calls about a room, filter Orders by room + today before saying “nothing pending.”'
      ),
      callout(
        'tip',
        'Night close',
        'Zero open unpaid orders before night close unless a documented city-ledger exception exists.'
      ),
    ],
    bestPractices: [
      bullets([
        'Zero open unpaid orders before night close.',
        'Refund only with manager approval and reason.',
        'Match e-bill amounts to what the guest was told verbally.',
        'Export/use Restaurant report for accountant detail.',
        'Flag repeated failed gateway payments to IT/finance.',
        'Keep room-charge and cash tickets clearly distinguished in handover notes.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Refunding without manager approval.',
        'Searching wrong date and declaring an order “missing.”',
        'Settling cash without capturing in POS.',
        'Ignoring failed payment status.',
        'Sending e-bill for the wrong order id.',
        'Closing Cash Counter before unpaid filter is empty.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Payment failed',
            'Network / gateway / tender',
            'Retry tender; check network/gateway; alternate method',
          ],
          [
            'Missing order',
            'Filters / wrong hotel',
            'Widen filters; check correct hotel context',
          ],
          [
            'E-bill not sending',
            'Guest contact / comms setup',
            'Verify mobile/email; retry; print bill instead',
          ],
          [
            'FO still sees balance',
            'Room charge not synced / unpaid',
            'Re-open order; settle; refresh booking folio',
          ],
          [
            'Audit missing',
            'Role permission',
            'Ask supervisor with access',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Draft invoice vs e-bill?',
          a: 'E-bill is guest-facing settlement copy; draft invoice tools support printable/fiscal needs depending on setup.',
        },
        {
          q: 'Can I edit items after KOT?',
          a: 'Follow void/comp rules with supervisor. Do not silently delete kitchen work already started.',
        },
        {
          q: 'Where do refunds appear?',
          a: 'On the order payment state and in restaurant/payments reports for the range.',
        },
        {
          q: 'How do I help checkout with POS bills?',
          a: 'Filter by room, settle unpaid, confirm with FO — see Check-out guide.',
        },
        {
          q: 'Is Orders the same as Restaurant ops?',
          a: 'Ops is live floor taking; Orders is the searchable back-office list and settlement follow-up.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pos-rooms',
    title: 'POS Room Service Rooms',
    moduleId: 'pos',
    summary:
      'Enable which hotel rooms can receive restaurant/room-service orders.',
    keywords: ['room service', 'pos rooms', 'enable rooms'],
    pathHint: 'POS → Room',
    related: ['restaurant-ops', 'check-in'],
    overview: [
      ...paragraphs(
        'POS Rooms controls which physical rooms appear for room-service ordering on the restaurant floor board. Configuration rooms must exist first; then you enable them here for F&B.',
        'If a newly opened wing is missing on Restaurant ops, cashiers often think POS is broken — usually the rooms were never enabled under POS → Room.',
        'Example: Hotel adds Room 501 in Configuration. F&B admin enables 501 for room service. After a guest checks in to 501, captains can select it for orders and KOT.',
        'Disable rooms that are OOO or not serviced so staff cannot mis-charge a dead room. Re-enable when Maintenance returns the room to sellable inventory and FO starts checking guests in again.',
        'Room service typically targets in-house rooms — follow your ops rules on the floor screen. Charging an empty or wrong room creates checkout disputes.'
      ),
    ],
    beforeYouBegin: [
      posPerm,
      bullets([
        'Rooms exist in Configuration / Property Setup',
        'Know which rooms offer room service (all vs select floors)',
        'Coordinate with HK/FO on OOO rooms to disable',
      ]),
    ],
    steps: [
      steps([
        { text: 'Confirm rooms exist in Configuration.' },
        { text: 'Open POS → Room.' },
        {
          text: 'Enable rooms that should accept room service.',
        },
        {
          text: 'Disable rooms that are OOO, under renovation, or not on room-service routes.',
        },
        {
          text: 'Verify enabled rooms appear on Restaurant ops.',
        },
        {
          text: 'Run a test order to an in-house enabled room (KOT + settle).',
        },
        {
          text: 'When Configuration adds new rooms later, return here to enable them.',
        },
        {
          text: 'Brief night cashiers whenever a floor is temporarily disabled.',
        },
      ]),
    ],
    screens: [
      bullets(
        ['Room enable list', 'Toggle active / enabled', 'Room identifiers matching FO'],
        'POS Rooms areas'
      ),
      fieldTable([
        ['Room list', '—', 'Physical rooms from configuration'],
        ['Enable / active toggle', 'Yes for service', 'Makes room selectable on floor'],
      ]),
    ],
    workflow: [
      workflow('Enable room service', [
        'Config rooms exist',
        'Enable in POS Rooms',
        'Guest checks in',
        'Test order to room',
        'Live room service',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Only enabled rooms are selectable for room orders on Restaurant ops.',
        'Enabling a room does not check a guest in — Booking Check-in still controls in-house status.',
        'Disabling OOO rooms prevents mis-charges while Maintenance works; it complements Housekeeping OOO and inventory blocks.',
        'Wrong-room charges are operational errors — enablement just exposes the room as a possible target.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'New rooms',
        'When you add a room in Configuration, enable it here if room service applies — same day.'
      ),
      callout(
        'tip',
        'OOO hygiene',
        'Disable OOO rooms in POS as soon as Maintenance opens a ticket.'
      ),
      callout(
        'note',
        'In-house still matters',
        'Enabled + vacant does not mean charge freely — follow in-house rules on the floor.'
      ),
    ],
    bestPractices: [
      bullets([
        'Disable OOO rooms in POS to prevent mis-charges.',
        'Mirror FO room numbering exactly (no “501A” vs “501”).',
        'Review enablement after renovations reopen.',
        'Test one order per new floor before marketing room service.',
        'Keep a checklist: Config room → POS enable → HK clean → FO sell.',
        'Tell captains which floors are temporarily off menu for service.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Forgetting to enable after opening a new wing.',
        'Leaving OOO rooms enabled.',
        'Assuming Configuration alone makes rooms appear on POS.',
        'Enabling rooms that never get room service (confusion on board).',
        'Charging rooms that look similar (501 vs 510) without read-back.',
        'Re-enabling after repair without telling FO/HK the room is back.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Room missing on floor',
            'Not enabled / not in config',
            'Enable in POS Rooms; confirm room exists in Configuration',
          ],
          [
            'Cannot order to occupied room',
            'Flow requires in-house / session',
            'Confirm check-in; refresh ops; follow SOP',
          ],
          [
            'Order hit wrong room',
            'Cashier selection error',
            'Void/transfer with supervisor; retrain read-back',
          ],
          [
            'OOO room still selectable',
            'Still enabled',
            'Disable in POS Rooms immediately',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Must guest be checked in?',
          a: 'Room service typically targets in-house rooms — follow your ops rules on the floor screen.',
        },
        {
          q: 'Is this the same as Housekeeping Room Status?',
          a: 'No. POS Rooms enables F&B targeting. HK Room Status controls clean/dirty sellability for FO keys.',
        },
        {
          q: 'Do I enable every hotel room?',
          a: 'Only rooms that should receive room service per property policy.',
        },
        {
          q: 'What if Configuration renumbers rooms?',
          a: 'Revisit POS Rooms enablement and retrain staff — stickers and POS must match.',
        },
        {
          q: 'Link to Check-in?',
          a: 'Guests become in-house via Check-in; POS then can take room service to that room when enabled.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pos-tables',
    title: 'POS Tables & QR Sessions',
    moduleId: 'pos',
    summary:
      'Create dining tables and manage QR ordering sessions for guest self-order.',
    keywords: ['tables', 'qr', 'qr session', 'dine in tables'],
    pathHint: 'POS → Table',
    related: ['pos-qr-ordering', 'restaurant-ops'],
    overview: [
      ...paragraphs(
        'Tables define dine-in locations on the restaurant floor board. Clear names that match physical stickers (T1, Garden-2) prevent captains from opening the wrong ticket.',
        'QR sessions let guests open a public menu linked to a table. Activating a session and printing the tent card must stay in sync — regenerating sessions without reprinting leaves guests on expired links.',
        'Example launch: Create tables T1–T12 → activate QR session for T7 → test scan on a phone → confirm menu items appear → print laminated QR for T7 only after the test passes.',
        'You can use tables for cashier POS without promoting QR. Disable or stop distributing QR links if you want staff-only ordering.',
        'Two physical tables must never share one digital table id — merge chaos and wrong KOTs follow.'
      ),
    ],
    beforeYouBegin: [
      posPerm,
      bullets([
        'Physical table map and sticker plan ready',
        'Menu active before QR testing',
        'Decide whether QR self-order is live or cashier-only',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open POS → Table.' },
        {
          text: 'Create tables with clear names/numbers matching physical stickers.',
        },
        {
          text: 'Verify tables appear on Restaurant ops floor board.',
        },
        {
          text: 'Generate or manage QR sessions for guest ordering where enabled.',
        },
        {
          text: 'Test scan → menu → add item → checkout path before printing tent cards.',
        },
        {
          text: 'Print and place QR tent cards only for tables that passed the test.',
        },
        {
          text: 'If you regenerate a session, reprint that table’s QR immediately and destroy old cards.',
        },
        {
          text: 'Train captains that cashier table names must match the QR label guests see.',
        },
      ]),
      callout(
        'warning',
        'Stale QR prints',
        'If you regenerate sessions, reprint table QRs or guests hit expired links.'
      ),
    ],
    screens: [
      bullets(
        ['Table CRUD', 'QR session controls', 'Table identifiers'],
        'Tables screen areas'
      ),
      fieldTable([
        ['Table name / number', 'Yes', 'Must match physical signage'],
        ['Create / edit / remove', '—', 'Keep map accurate'],
        ['QR session', 'If using QR', 'Links public menu to this table'],
      ]),
    ],
    workflow: [
      workflow('Launch QR table', [
        'Create table',
        'Enable QR session',
        'Test scan',
        'Print QR',
        'Guest scans',
        'Kitchen sees order',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'QR sessions associate guest carts with a table context for the public ordering pages.',
        'Restaurant ops uses the same table master for cashier-taken orders — one map, two ways to open a ticket.',
        'Expired or regenerated sessions break printed materials; operational truth is the active session, not the laminated card.',
        'Deleting tables that have open orders is unsafe — settle or move orders first per SOP.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Name like signage',
        'Name tables exactly as signage (T1, Garden-2). Creative names that staff invent mid-shift cause wrong KOTs.'
      ),
      callout(
        'tip',
        'Weekly QR check',
        'Once a week, scan a sample of tent cards to confirm links still open.'
      ),
      callout(
        'note',
        'Cashier-only mode',
        'You can keep tables for POS without promoting QR links to guests.'
      ),
    ],
    bestPractices: [
      bullets([
        'Name tables exactly as signage (T1, Garden-2).',
        'Test before print — always.',
        'Destroy obsolete QR cards after regeneration.',
        'Map outdoor vs indoor tables clearly for captains.',
        'Avoid reusing table names for different physical locations.',
        'Document which tables are QR-enabled vs cashier-only.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Two physical tables sharing one digital table.',
        'Printing QR before menu is ready.',
        'Regenerating sessions without reprinting.',
        'Renaming tables mid-service without updating stickers.',
        'Leaving QR enabled on a decommissioned table.',
        'Assuming QR orders skip kitchen KOT process — staff must still fulfil.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'QR not opening',
            'Session inactive / bad URL',
            'Check session active + public URL; retest',
          ],
          [
            'Wrong table',
            'Tent card mismatch',
            'Verify tent card matches table id; reprint',
          ],
          [
            'Table missing on floor',
            'Not created / filtered',
            'Create table; refresh Restaurant ops',
          ],
          [
            'Guest order not visible',
            'Wrong session / fulfilment lag',
            'Check Orders list; confirm session; assist via cashier POS',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I disable QR but keep cashier POS?',
          a: 'Yes — use tables for cashier ops without promoting QR links.',
        },
        {
          q: 'Do QR orders appear in Restaurant ops?',
          a: 'Orders land in POS for kitchen and settlement follow-up — monitor Orders / floor process per your setup.',
        },
        {
          q: 'How many QR sessions per table?',
          a: 'Treat one active session per table as the operational rule; regenerate carefully and reprint.',
        },
        {
          q: 'Can two QR cards point to one table?',
          a: 'Only if identical active session copies — still prefer one clear card per seat cluster to avoid confusion.',
        },
        {
          q: 'Related guide for guest flow?',
          a: 'See QR Menu & Open Ordering for the guest scan → cart → checkout path.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pos-qr-ordering',
    title: 'QR Menu & Open Ordering',
    moduleId: 'pos',
    summary:
      'Guest-facing QR menu, cart, and checkout without a cashier at the table.',
    keywords: [
      'qr ordering',
      'qr menu',
      'open ordering',
      'guest order',
    ],
    pathHint: 'Public QR links / POS tables',
    related: ['pos-tables', 'pos-menu', 'pos-orders'],
    overview: [
      ...paragraphs(
        'QR Open Ordering pages let guests browse the menu, add items to a cart, and checkout. Orders land in POS for kitchen fulfilment and settlement follow-up.',
        'This is not NexaStay (hotel booking). It is restaurant self-order tied to a table QR session. Guests still need allergen conversations — a QR menu does not replace asking about allergies.',
        'Example: Guest at T4 scans tent card → public menu loads → adds Paneer Tikka and Soft Drink → checkout → payment per configured method → kitchen sees the order → staff serve → exceptions handled in Orders.',
        'Launch order: Menu ready → table QR session active → test full path → print cards. Printing first guarantees empty menus and support calls.',
        'Failed checkouts need staff backup: captains should be ready to take the same order on Restaurant ops if the guest’s payment fails.'
      ),
    ],
    beforeYouBegin: [
      posPerm,
      bullets([
        'Menu items active and priced correctly',
        'Tables / QR sessions active',
        'Payment method for QR decided and tested',
        'Kitchen staffing ready to fulfil unattended orders',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Ensure Menu items are active and categories make sense on a phone screen.',
        },
        {
          text: 'Activate QR session for the table (POS → Table).',
        },
        {
          text: 'Test on a real phone: scan → browse → add to cart → checkout.',
        },
        {
          text: 'Confirm the order appears for staff (Orders / kitchen process).',
        },
        {
          text: 'Only then print/laminate guest QR tent cards.',
        },
        {
          text: 'During service, monitor Orders for new QR tickets and fulfil like any KOT-driven order.',
        },
        {
          text: 'Help guests with failed checkouts by switching to cashier POS on the same table.',
        },
        {
          text: 'Weekly: re-scan sample QRs; remove cards for inactive sessions.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Public menu page',
          'Cart',
          'Checkout page',
          'Staff order list / ops fulfilment',
        ],
        'Guest + staff surfaces'
      ),
      fieldTable([
        ['QR link / session', 'Yes', 'Ties guest cart to a table'],
        ['Menu browse', '—', 'Active items from Menu'],
        ['Cart', '—', 'Guest selections before pay'],
        ['Checkout', 'Yes to place', 'Payment / order submit'],
        ['Staff Orders', '—', 'Fulfilment and settlement follow-up'],
      ]),
    ],
    workflow: [
      workflow('Guest self-order', [
        'Scan QR',
        'Add items',
        'Checkout',
        'Kitchen prepares',
        'Serve',
        'Handle exceptions in Orders',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Public pages are unauthenticated guest flows tied to table sessions. Anyone with the link can open that table’s ordering context — protect printed cards.',
        'Payment success/failure follows configured payment handling. Failed payments should not be assumed as placed orders.',
        'Menu catalogue is shared with cashier POS; inactive items disappear for guests too.',
        'Staff remain responsible for allergens, alcohol policy, and refusing impossible 86’d items even if a guest ordered them moments before deactivation.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Peak hours',
        'Post a clear “ask staff for allergens” note; QR menus should not replace allergen conversations.'
      ),
      callout(
        'tip',
        'Backup path',
        'Train captains to take the order on POS if QR checkout fails — do not leave guests stuck.'
      ),
      callout(
        'note',
        'Not NexaStay',
        'NexaStay is hotel booking. QR ordering is restaurant POS only.'
      ),
    ],
    bestPractices: [
      bullets([
        'Laminate QRs; check weekly that links still open.',
        'Monitor failed checkouts.',
        'Keep menu photos/names mobile-friendly.',
        'Disable QR on tables you cannot staff to fulfil.',
        'Align alcohol/ID checks with local law even for self-order.',
        'Destroy old QR cards after session regeneration.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Printing QR before menu is ready.',
        'No staff watching the Orders list during QR service.',
        'Assuming QR payment success without checking Orders.',
        'Leaving allergen duty entirely to the phone UI.',
        'Mixing NexaStay booking QR with restaurant QR on the same stand.',
        'Forgetting to 86 items on Menu when stock runs out.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Menu empty',
            'Items inactive',
            'Activate items on Menu; retest',
          ],
          [
            'Checkout error',
            'Payment / session issue',
            'Retry payment; assist via cashier POS',
          ],
          [
            'Expired link',
            'Session regenerated',
            'Reprint QR from active session',
          ],
          [
            'Order not cooking',
            'Staff not monitoring',
            'Watch Orders; send KOT/print per kitchen SOP',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is this NexaStay?',
          a: 'No. NexaStay is the hotel booking engine. QR ordering is restaurant POS.',
        },
        {
          q: 'Can guests order to a room via QR?',
          a: 'This flow is table QR open ordering. Room service is typically cashier/ops selecting POS Rooms.',
        },
        {
          q: 'Do guests need an account?',
          a: 'Public QR pages are guest flows tied to table sessions — no staff login on the guest phone.',
        },
        {
          q: 'Where do I refund a QR order?',
          a: 'Via POS Orders / payment refund flows with supervisor approval.',
        },
        {
          q: 'What if two guests scan the same table?',
          a: 'They share the table ordering context — captains should manage seat clusters and timing like any busy table.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pos-payments',
    title: 'Restaurant Payments & Refunds',
    moduleId: 'pos',
    summary:
      'Capture, fail, or refund restaurant order payments from the payment drawer.',
    keywords: [
      'pos payment',
      'refund',
      'e-bill',
      'restaurant settle',
    ],
    pathHint: 'Orders / Order drawer → Payment',
    related: ['pos-orders', 'cash-counter', 'check-out'],
    overview: [
      ...paragraphs(
        'The payment drawer records how a restaurant order was settled — cash, card, UPI, room charge, and other configured tenders — and supports fail/refund paths when needed.',
        'Capturing payment is what marks orders paid for reports. Taking cash in hand without capturing in POS breaks Cash Counter reconciliation and FO checkout trust.',
        'Example settle table: Present bill for T3 → guest pays UPI → capture UPI for full amount → send e-bill → close order. Example room charge: in-house 118 dinner → choose room charge → FO sees it at Check-out — tell FO if needed.',
        'Refunds reverse revenue and must stay audited. Limit refund authority to supervisors. Refunding to the wrong method (cash vs original UPI) creates finance noise.',
        'Room charge is not “free food” — it moves collection to the booking folio. Guests still pay at checkout unless company billing rules apply.'
      ),
    ],
    beforeYouBegin: [
      posPerm,
      permissionTable([
        [
          'Payment/refund authority',
          'Limit refunds and comps to supervisors',
        ],
      ]),
      bullets([
        'Know accepted tenders for the shift',
        'For room charge, confirm guest is in-house and FO aware',
        'Cash Counter session practices understood for reconciliation',
      ]),
      callout(
        'important',
        'Room charge',
        'If charging to room, ensure the booking is in-house and FO knows at checkout.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open the order payment drawer from Restaurant ops or POS → Orders.',
        },
        {
          text: 'Confirm the amount due matches the bill presented to the guest.',
        },
        {
          text: 'Choose method and amount (full or partial tenders where supported).',
        },
        {
          text: 'Confirm capture. Wait for success — do not assume gateway success from a phone notification alone.',
        },
        {
          text: 'Send e-bill or print receipt if required.',
        },
        {
          text: 'For failed payments, retry or switch tender; leave status accurate.',
        },
        {
          text: 'For refunds, use refund action with reason and supervisor authorisation.',
        },
        {
          text: 'Reconcile captures and refunds with Cash Counter at shift end.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Method selector',
          'Amount',
          'Capture / fail / refund actions',
          'Receipt / e-bill actions',
        ],
        'Payment drawer areas'
      ),
      fieldTable([
        ['Tender / method', 'Yes', 'How the guest pays'],
        ['Amount', 'Yes', 'What is captured'],
        ['Capture', 'Yes to pay', 'Marks successful settlement'],
        ['Fail / retry', 'When needed', 'Keeps status honest'],
        ['Refund', 'Authorised', 'Reverses revenue with audit'],
        ['E-bill / receipt', 'Policy', 'Guest proof of payment'],
      ]),
    ],
    workflow: [
      workflow('Settle table', [
        'Present bill',
        'Take tender',
        'Capture payment',
        'E-bill / receipt',
        'Close order',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Successful captures mark orders paid for restaurant reports and clear operational blockers on tables/rooms.',
        'Refunds reverse revenue and must stay audited. Unauthorised cash refunds outside POS break Cash Counter.',
        'Room charge shifts collection timing to the booking checkout/payments path — coordinate with FO Check-out.',
        'Partial tenders (where supported) still require the order to reach a clear paid state before you treat the table as closed.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Match the bill',
        'Read the total aloud before capturing. Silent UPI captures for the wrong amount cause refunds.'
      ),
      callout(
        'tip',
        'Shift end',
        'Reconcile POS payments with Cash Counter sessions daily — same day, not next week.'
      ),
      callout(
        'warning',
        'Do not fake paid',
        'Never mark paid without collecting. It steals from night audit and the next cashier.'
      ),
    ],
    bestPractices: [
      bullets([
        'Reconcile POS payments with Cash Counter sessions daily.',
        'Limit refund permissions to supervisors.',
        'Document refund reasons for every case.',
        'Tell FO immediately about large room charges near checkout time.',
        'Prefer e-bill when guests need expense proofs.',
        'Investigate double charges via order audit before refunding twice.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Marking paid without collecting cash.',
        'Refunding to the wrong method.',
        'Room charging the wrong room.',
        'Ignoring failed payment and seating the next guests.',
        'Refunding without audit/reason.',
        'Closing Cash Counter before POS unpaid list is clean.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Refund blocked',
            'Permission or status',
            'Ask supervisor; confirm order state allows refund',
          ],
          [
            'Double charge',
            'Retry captured twice',
            'Check audit; refund duplicate with approval',
          ],
          [
            'Room charge not on folio',
            'Wrong room / not posted',
            'Verify order room; settle path; refresh booking; alert FO',
          ],
          [
            'Gateway timeout',
            'Network',
            'Check if capture succeeded before retrying; avoid double charge',
          ],
          [
            'Cash mismatch',
            'Offline cash not captured',
            'Post missing captures; retrain; reconcile same shift',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Where do refunds appear in reports?',
          a: 'Restaurant/payments reports reflect payment lines including refunds when exported for the range.',
        },
        {
          q: 'Is room charge a payment method?',
          a: 'It settles the restaurant order by moving the balance to the in-house booking path — guest still pays at checkout unless company billing applies.',
        },
        {
          q: 'Can I split payment across UPI and cash?',
          a: 'Where partial tenders are supported, yes — follow on-screen amounts and close to paid.',
        },
        {
          q: 'Link to Check-out?',
          a: 'Open room-charge and unpaid room orders must be cleared so FO Check-out balances are correct.',
        },
        {
          q: 'Who uses Cash Counter?',
          a: 'Cashiers/finance reconcile tenders — POS captures should match Cash Counter sessions.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pos-dashboard',
    title: 'POS Dashboard',
    moduleId: 'pos',
    summary: 'Restaurant sales and order analytics for F&B managers.',
    keywords: [
      'pos dashboard',
      'restaurant analytics',
      'f&b sales',
    ],
    pathHint: 'POS → Dashboard',
    related: ['pos-orders', 'reports-restaurant'],
    overview: [
      ...paragraphs(
        'POS Dashboard summarises restaurant performance — sales, orders, and trends — separate from room revenue dashboards on the hotel home.',
        'Use it for F&B management questions: Was Friday dinner stronger than last week? Did QR launch increase ticket count? Are refunds spiking?',
        'Example weekly review: Open POS Dashboard → set last 7 days → note peak hours and average ticket → spot a dip on Tuesday lunch → check Orders for voids/stock-outs → adjust staffing/menu.',
        'Dashboard KPIs are not a replacement for accountant exports. Use Reports → Restaurant when you need line-level or finance-ready detail.',
        'Always confirm hotel context and date range. “Zero sales” is often the wrong day filter or unpaid orders never captured — not an empty restaurant.'
      ),
    ],
    beforeYouBegin: [
      posPerm,
      bullets([
        'Know which date range you want (today vs week vs month)',
        'Confirm you are in the correct hotel',
        'Have Restaurant report access for deep dives',
      ]),
    ],
    steps: [
      steps([
        { text: 'Open POS → Dashboard.' },
        { text: 'Set the date range you care about.' },
        {
          text: 'Review sales/order widgets and any peak-hour or trend charts shown.',
        },
        {
          text: 'Compare against last week/period for the same days.',
        },
        {
          text: 'Investigate dips: menu 86s, staffing, QR issues, payment failures (use Orders).',
        },
        {
          text: 'Export or open Reports → Restaurant for accountant-ready detail when needed.',
        },
        {
          text: 'Share one-page insights with FO/GM without mixing room rent into F&B numbers.',
        },
        {
          text: 'Schedule a monthly review of average ticket size and peak hours for roster planning.',
        },
      ]),
    ],
    screens: [
      bullets(
        ['KPI cards', 'Charts', 'Date filters'],
        'Dashboard elements'
      ),
      fieldTable([
        ['Date range', '—', 'Scopes all widgets'],
        ['Sales KPIs', '—', 'Revenue-oriented summaries'],
        ['Order KPIs', '—', 'Volume / ticket trends'],
        ['Charts', '—', 'Visual peaks and comparisons'],
      ]),
    ],
    workflow: [
      workflow('Weekly F&B review', [
        'Open POS dashboard',
        'Compare weeks',
        'Inspect Orders for anomalies',
        'Adjust menu/staffing',
        'Confirm in Restaurant report',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Aggregates paid order activity for the hotel POS. Unpaid or failed tickets may not count as sales — clean settlement hygiene improves dashboard truth.',
        'Room rent and booking revenue live elsewhere; do not brief owners with mixed numbers from the wrong dashboard.',
        'Refunds reduce net performance — a busy refund day can look like soft sales if you only glance at totals.',
        'Hotel context matters in multi-property organisations: always read the header property name.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Pair with Reports',
        'Export Restaurant report for accountant-ready detail after you spot a trend here.'
      ),
      callout(
        'tip',
        'Peak staffing',
        'Use peak-hour patterns to roster captains and kitchen before weekends.'
      ),
      callout(
        'note',
        'Not room revenue',
        'This dashboard is restaurant/POS oriented — keep FO room KPIs separate.'
      ),
    ],
    bestPractices: [
      bullets([
        'Review average ticket size and peak hours monthly.',
        'Investigate anomalies the same day while Orders audit is fresh.',
        'Brief owners with F&B-only numbers from POS/reports.',
        'Track QR launch weeks separately to measure lift.',
        'Combine dashboard trends with item-level report insights.',
        'Do not chase vanity volume if refunds and voids are high.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Mixing room revenue with F&B when briefing owners.',
        'Reading widgets without checking date range.',
        'Ignoring unpaid settlement problems that understate sales.',
        'Changing menu prices without watching ticket size impact.',
        'Assuming zero sales means no guests — check filters/hotel first.',
        'Skipping Restaurant report when finance asks for detail.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Zero sales',
            'Wrong range / hotel / unpaid captures',
            'Confirm orders paid in range; check hotel; settle unpaid',
          ],
          [
            'Numbers disagree with report',
            'Different filters or refund timing',
            'Align date range; include refunds; use Reports → Restaurant',
          ],
          [
            'Dashboard empty widgets',
            'No POS volume yet / permission',
            'Confirm module use; refresh; check permission',
          ],
          [
            'Sudden spike',
            'Event / duplicate captures',
            'Inspect Orders audit for doubles before celebrating',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Does it include room rent?',
          a: 'No — this is restaurant/POS oriented.',
        },
        {
          q: 'Where is item-level detail?',
          a: 'Use Reports → Restaurant and Orders for line-level investigation.',
        },
        {
          q: 'Can cashiers use this?',
          a: 'If permitted, yes — but day-to-day settlement still happens in ops/Orders.',
        },
        {
          q: 'How often should managers check?',
          a: 'Daily flash for today; deeper weekly compare for staffing/menu.',
        },
        {
          q: 'Related guides?',
          a: 'POS Orders for unpaid cleanup; Reports Restaurant for exports.',
        },
      ]),
    ],
  }),
];
